import { profile } from '@/content/profile';
import { experienceRoles } from '@/content/experience';
import { absoluteUrl, siteConfig } from '@/seo/siteConfig';

/** Every JSON-LD block carries a `@type`, which doubles as its React key. */
export type JsonLdEntry = Record<string, unknown> & { '@type': string };

/**
 * `Person` structured data built from the same approved content as the pages.
 * Nothing here may assert anything the CV does not.
 */
export function buildPersonJsonLd(): JsonLdEntry {
  const linkedIn = profile.links.find((link) => link.id === 'linkedin');
  const currentRole = experienceRoles[0];

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: profile.name,
    jobTitle: profile.role,
    description: profile.summary,
    email: `mailto:${profile.email}`,
    url: siteConfig.url,
    ...(profile.photo ? { image: absoluteUrl(profile.photo.socialSrc) } : {}),
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Iasi',
      addressCountry: 'RO',
    },
    alumniOf: {
      '@type': 'CollegeOrUniversity',
      name: 'Alexandru Ioan Cuza University of Iasi',
    },
    ...(currentRole ? { worksFor: { '@type': 'Organization', name: currentRole.company } } : {}),
    knowsAbout: [
      'React',
      'TypeScript',
      'Next.js',
      'Frontend architecture',
      'Design systems',
      'Web performance',
      'Web accessibility',
      'Generative AI',
      'LLM workflows',
      'Prompt engineering',
    ],
    knowsLanguage: profile.languages.map((language) => language.name),
    ...(linkedIn ? { sameAs: [linkedIn.href] } : {}),
  };
}

export function buildWebSiteJsonLd(): JsonLdEntry {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    inLanguage: siteConfig.locale,
  };
}

export function buildBreadcrumbJsonLd(
  trail: readonly { name: string; path: string }[],
): JsonLdEntry {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
